import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

export interface CashRegisterReportData {
  cashRegister: {
    id: string;
    openedAt: string;
    closedAt: string | null;
    openingAmount: number;
    closingAmount: number | null;
    expectedAmount: number | null;
    difference: number | null;
    notes: string | null;
    openedBy: string | null;
    closedBy: string | null;
  };
  cashCount: {
    denomination: number;
    quantity: number;
    total: number;
    type: string;
  }[];
  paymentMethodCounts: {
    paymentMethodId: string;
    paymentMethodName: string;
    countedAmount: number;
  }[];
  salesByPaymentMethod: {
    paymentMethodId: string;
    paymentMethodName: string;
    totalSales: number;
    orderCount: number;
  }[];
  salesByOrderType: {
    orderType: "table" | "tab" | "counter" | "delivery";
    label: string;
    totalSales: number;
    orderCount: number;
    sources?: { source: string; label: string; totalSales: number; orderCount: number }[];
  }[];
  movements: {
    type: "supply" | "withdrawal";
    amount: number;
    reason: string | null;
    createdAt: string;
  }[];
  totals: {
    supplies: number;
    withdrawals: number;
    totalCashCounted: number;
    totalPaymentMethodsCounted: number;
    totalSales: number;
    totalCashSales: number;
    totalGrossSales: number;
  };
}

export function useCashRegisterReport() {
  const { activeRestaurant } = useRestaurantContext();
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState<CashRegisterReportData | null>(null);

  const fetchReport = useCallback(
    async (cashRegisterId: string) => {
      if (!activeRestaurant) return null;

      setIsLoading(true);
      try {
        // Fetch cash register
        const { data: register, error: registerError } = await supabase
          .from("cash_registers")
          .select(`
            *,
            opened_by_profile:profiles!cash_registers_opened_by_fkey(full_name),
            closed_by_profile:profiles!cash_registers_closed_by_fkey(full_name)
          `)
          .eq("id", cashRegisterId)
          .maybeSingle();

        if (registerError) {
          console.error("Error fetching register:", registerError);
          toast.error("Erro ao carregar dados do caixa");
          return null;
        }
        if (!register) {
          toast.error("Caixa não encontrado");
          return null;
        }

        // Fetch cash count details
        const { data: cashCountData, error: cashCountError } = await supabase
          .from("cash_count_details")
          .select("*")
          .eq("cash_register_id", cashRegisterId);
        if (cashCountError) console.warn("Erro ao carregar contagem de dinheiro:", cashCountError);

        // Fetch payment method counts
        const { data: paymentCountsData, error: paymentCountsError } = await supabase
          .from("cash_register_payment_counts")
          .select("*, payment_methods(name)")
          .eq("cash_register_id", cashRegisterId);
        if (paymentCountsError) console.warn("Erro ao carregar contagem de pagamentos:", paymentCountsError);

        // Fetch movements
        const { data: movementsData, error: movementsError } = await supabase
          .from("cash_movements")
          .select("*")
          .eq("cash_register_id", cashRegisterId)
          .order("created_at", { ascending: true });
        if (movementsError) console.warn("Erro ao carregar movimentações:", movementsError);

        // Fetch sales by payment method during this cash register period
        const { data: salesData } = await supabase
          .from("order_payments")
          .select(`
            payment_method_id,
            amount,
            orders!inner(
              id,
              status,
              created_at,
              restaurant_id
            ),
            payment_methods(name)
          `)
          .eq("orders.restaurant_id", activeRestaurant.id)
          .in("orders.status", ["paid", "delivered"])
          .gte("orders.created_at", register.opened_at)
          .lte("orders.created_at", register.closed_at || new Date().toISOString());

        // Fetch partial payments during this period
        const { data: partialPaymentsData } = await supabase
          .from("partial_payments")
          .select(`
            payment_method_id,
            amount,
            created_at,
            payment_methods(name)
          `)
          .eq("restaurant_id", activeRestaurant.id)
          .gte("created_at", register.opened_at)
          .lte("created_at", register.closed_at || new Date().toISOString());

        // Fetch orders in period for breakdown by order_type (and synthetic iFood payment row)
        // NOTE: Some delivery orders (iFood) may have subtotal/total = 0 due to sync issues.
        // They are intentionally NOT filtered out — they show as R$ 0,00.
        const { data: periodOrdersForBreakdown } = await supabase
          .from("orders")
          .select("id, order_type, total, status, external_source")
          .eq("restaurant_id", activeRestaurant.id)
          .in("status", ["paid", "delivered"])
          .gte("created_at", register.opened_at)
          .lte("created_at", register.closed_at || new Date().toISOString());

        // Get payment methods to include cash
        const { data: paymentMethodsData } = await supabase
          .from("payment_methods")
          .select("id, name, type")
          .eq("restaurant_id", activeRestaurant.id);

        // Aggregate sales by payment method
        const salesByMethod = new Map<string, { name: string; total: number; count: number; type: string }>();

        // Initialize with all payment methods
        (paymentMethodsData || []).forEach((pm: any) => {
          salesByMethod.set(pm.id, { name: pm.name, total: 0, count: 0, type: pm.type });
        });

        // Add order payments
        (salesData || []).forEach((payment: any) => {
          const existing = salesByMethod.get(payment.payment_method_id);
          if (existing) {
            existing.total += Number(payment.amount);
            existing.count += 1;
          }
        });

        // Add partial payments
        (partialPaymentsData || []).forEach((payment: any) => {
          const existing = salesByMethod.get(payment.payment_method_id);
          if (existing) {
            existing.total += Number(payment.amount);
            existing.count += 1;
          }
        });

        // === Build salesByOrderType breakdown ===
        const typeLabels: Record<string, string> = {
          table: "Mesa",
          tab: "Comanda",
          counter: "Balcão",
          delivery: "Delivery",
        };
        const sourceLabels: Record<string, string> = {
          ifood: "iFood",
          manual: "Manual",
        };
        const typeAgg = new Map<string, { total: number; count: number; sources: Map<string, { total: number; count: number }> }>();
        (["table", "tab", "counter", "delivery"] as const).forEach((t) =>
          typeAgg.set(t, { total: 0, count: 0, sources: new Map() })
        );
        let ifoodDeliveryTotal = 0;
        let ifoodDeliveryCount = 0;

        (periodOrdersForBreakdown || []).forEach((o: any) => {
          const key = (o.order_type as string) || "counter";
          const bucket = typeAgg.get(key);
          if (!bucket) return;
          const total = Number(o.total) || 0;
          bucket.total += total;
          bucket.count += 1;

          if (key === "delivery") {
            const src = (o.external_source as string) || "manual";
            const sBucket = bucket.sources.get(src) || { total: 0, count: 0 };
            sBucket.total += total;
            sBucket.count += 1;
            bucket.sources.set(src, sBucket);

            // iFood deliveries are paid on the platform — they don't generate order_payments.
            // Add a synthetic payment-method line so the totals reconcile.
            if (src === "ifood") {
              ifoodDeliveryTotal += total;
              ifoodDeliveryCount += 1;
            }
          }
        });

        const salesByOrderType = Array.from(typeAgg.entries()).map(([orderType, v]) => ({
          orderType: orderType as "table" | "tab" | "counter" | "delivery",
          label: typeLabels[orderType] || orderType,
          totalSales: v.total,
          orderCount: v.count,
          sources: orderType === "delivery"
            ? Array.from(v.sources.entries()).map(([source, s]) => ({
                source,
                label: sourceLabels[source] || source,
                totalSales: s.total,
                orderCount: s.count,
              }))
            : undefined,
        }));

        // Calculate totals
        const supplies = (movementsData || [])
          .filter((m: any) => m.type === "supply")
          .reduce((sum: number, m: any) => sum + Number(m.amount), 0);

        const withdrawals = (movementsData || [])
          .filter((m: any) => m.type === "withdrawal")
          .reduce((sum: number, m: any) => sum + Number(m.amount), 0);

        const totalCashCounted = (cashCountData || []).reduce(
          (sum: number, c: any) => sum + Number(c.total || c.denomination * c.quantity),
          0
        );

        const totalPaymentMethodsCounted = (paymentCountsData || []).reduce(
          (sum: number, p: any) => sum + Number(p.counted_amount),
          0
        );

        const totalSales = Array.from(salesByMethod.values()).reduce(
          (sum, v) => sum + v.total,
          0
        );

        const totalCashSales = Array.from(salesByMethod.entries())
          .filter(([_, v]) => v.type === "cash")
          .reduce((sum, [_, v]) => sum + v.total, 0);

        // Gross sales = sum of all orders by type (includes iFood delivery not in order_payments)
        const totalGrossSales = salesByOrderType.reduce((sum, t) => sum + t.totalSales, 0);

        // Build payment method list, appending synthetic iFood line when applicable
        const salesByPaymentMethodList = Array.from(salesByMethod.entries())
          .filter(([_, v]) => v.total > 0 || v.type !== "cash")
          .map(([id, v]) => ({
            paymentMethodId: id,
            paymentMethodName: v.name,
            totalSales: v.total,
            orderCount: v.count,
          }));
        if (ifoodDeliveryTotal > 0 || ifoodDeliveryCount > 0) {
          salesByPaymentMethodList.push({
            paymentMethodId: "__synthetic_ifood__",
            paymentMethodName: "Delivery iFood",
            totalSales: ifoodDeliveryTotal,
            orderCount: ifoodDeliveryCount,
          });
        }

        const report: CashRegisterReportData = {
          cashRegister: {
            id: register.id,
            openedAt: register.opened_at,
            closedAt: register.closed_at,
            openingAmount: Number(register.opening_amount),
            closingAmount: register.closing_amount ? Number(register.closing_amount) : null,
            expectedAmount: register.expected_amount ? Number(register.expected_amount) : null,
            difference: register.difference ? Number(register.difference) : null,
            notes: register.notes,
            openedBy: (register as any).opened_by_profile?.full_name || null,
            closedBy: (register as any).closed_by_profile?.full_name || null,
          },
          cashCount: (cashCountData || []).map((c: any) => ({
            denomination: Number(c.denomination),
            quantity: c.quantity,
            total: Number(c.total || c.denomination * c.quantity),
            type: c.type,
          })),
          paymentMethodCounts: (paymentCountsData || []).map((p: any) => ({
            paymentMethodId: p.payment_method_id,
            paymentMethodName: p.payment_methods?.name || "Desconhecido",
            countedAmount: Number(p.counted_amount),
          })),
          salesByPaymentMethod: salesByPaymentMethodList,
          salesByOrderType,
          movements: (movementsData || []).map((m: any) => ({
            type: m.type,
            amount: Number(m.amount),
            reason: m.reason,
            createdAt: m.created_at,
          })),
          totals: {
            supplies,
            withdrawals,
            totalCashCounted,
            totalPaymentMethodsCounted,
            totalSales: totalSales + ifoodDeliveryTotal,
            totalCashSales,
            totalGrossSales,
          },
        };

        setReportData(report);
        return report;
      } catch (error) {
        console.error("Error fetching cash register report:", error);
        toast.error("Erro ao gerar relatório do caixa");
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [activeRestaurant]
  );

  return {
    reportData,
    isLoading,
    fetchReport,
  };
}
